<template>
  <Survey v-if="survey" :survey="survey" />
</template>

<script>
import 'survey-core/defaultV2.min.css';
import { Model } from 'survey-core';
import { Survey } from 'survey-vue-ui';
import { ref, computed, getCurrentInstance, watch } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import darkTheme from '@/assets/surveyThemes/survey_theme_dark.json';
import lightTheme from '@/assets/surveyThemes/survey_theme_light.json';
import router from '@/router';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const themes = {
  light: lightTheme,
  dark: darkTheme
};

export default {
  components: {
    Survey
  },
  setup() {
    const { skin } = useAppConfig();
    const surveySkin = computed(() => skin.value);

    const id = router.currentRoute.params.id;
    const item = ref(null);
    const survey = ref(null);
    const ratingDescription = ref('');
    let descs = {};

    const { proxy } = getCurrentInstance();

    // Helper to get the description for a given question and rating value
    function getDescriptionForRating(question, value) {
      if (!value) return 'Please hover over the rating choices below for descriptions.';
      const page = survey.value.getPageByQuestion(question);
      const competency = page?.navigationTitle || page?.title;
      const subcompetency = question.title;
      const rateObj = question.rateValues.find(opt => opt.value.toString() === value.toString());
      const ratingText = rateObj ? rateObj.text : value;
      return (
        descs?.[competency]?.[subcompetency]?.[ratingText] ||
        descs?.[competency]?.[subcompetency]?.[value] ||
        ''
      );
    }

    proxy.$http.get(`/surveys/${id}`).then((res) => {
      item.value = res.data;
      const surveyJSON = item.value.body;
      surveyJSON['width'] = 1200;
      surveyJSON['title'] += item.value.reviewee.fullName ? ` - ${item.value.reviewee.fullName}` : '';
      // Fetch rating descriptions
      const category = item.value.category;
      proxy.$http.get(`/constants/rating-descriptions/${category}`).then((descRes) => {
        descs = descRes.data;
      });
      // Quick hack to fix the text elements in the survey not scrollable
      surveyJSON.pages.forEach(page => {
        page.elements.forEach(element => {
          if (element.type === 'text') {
            element.type='comment';
          }
        });
      });
      survey.value = new Model(surveyJSON);
      survey.value.data = item.value.answers;
      survey.value.applyTheme(themes[surveySkin.value]);
      survey.value.mode = "display";
      // Attach listeners to rating items after each question is rendered
      survey.value.onAfterRenderQuestion.add((surveyInstance, options) => {
        if (options.question.getType() === 'rating') {
          const items = options.htmlElement.querySelectorAll('.sd-rating__item');
          items.forEach((itemEl, idx) => {
            itemEl.onmouseenter = null;
            itemEl.onmouseleave = null;
            itemEl.onclick = null;
            let tooltip = itemEl.querySelector('.custom-tooltip');
            if (tooltip) itemEl.removeChild(tooltip);
            const input = itemEl.querySelector('input[type=radio]');
            let value = input ? input.value : null;
            tooltip = document.createElement('div');
            tooltip.className = 'custom-tooltip';
            tooltip.style.display = 'none';
            tooltip.style.position = 'fixed';
            tooltip.style.zIndex = 1000;
            tooltip.style.background = 'rgba(30, 30, 40, 0.98)';
            tooltip.style.color = '#fff';
            tooltip.style.padding = '20px 22px';
            tooltip.style.borderRadius = '10px';
            tooltip.style.fontSize = '1.01em';
            tooltip.style.whiteSpace = 'pre-line';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.maxWidth = '600px';
            tooltip.style.minWidth = '320px';
            tooltip.style.boxShadow = '0 8px 32px rgba(0,0,0,0.28)';
            tooltip.style.lineHeight = '1.7';
            tooltip.style.textAlign = 'left';
            tooltip.style.wordBreak = 'break-word';
            tooltip.style.transition = 'opacity 0.22s cubic-bezier(.4,0,.2,1)';
            tooltip.style.opacity = '0';
            tooltip.style.overflowY = 'auto';
            tooltip.style.maxHeight = '320px';
            tooltip.style.border = '1.5px solid #444';
            tooltip.style.left = '0';
            tooltip.style.top = '0';
            itemEl.appendChild(tooltip);
            itemEl.onmouseenter = (e) => {
              const desc = getDescriptionForRating(options.question, value) || '';
              if (desc) {
                tooltip.textContent = desc;
                tooltip.style.display = 'block';
                tooltip.style.opacity = '1';
                const rect = itemEl.getBoundingClientRect();
                const tooltipWidth = Math.min(tooltip.offsetWidth || 600, 600);
                const tooltipHeight = tooltip.offsetHeight || 60;
                let left = rect.left + rect.width / 2 - tooltipWidth / 2;
                left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
                let top;
                if (rect.top > tooltipHeight + 20) {
                  top = rect.top - tooltipHeight - 14;
                } else {
                  top = rect.bottom + 10;
                }
                top = Math.max(8, Math.min(top, window.innerHeight - tooltipHeight - 8));
                tooltip.style.left = left + 'px';
                tooltip.style.top = top + 'px';
              } else {
                tooltip.style.display = 'none';
                tooltip.style.opacity = '0';
              }
            };
            itemEl.onmouseleave = () => {
              tooltip.style.opacity = '0';
              setTimeout(() => { tooltip.style.display = 'none'; }, 180);
            };
            itemEl.onclick = () => {
              const desc = getDescriptionForRating(options.question, value) || '';
            };
          });
        }
      });
      // Update question.description dynamically on value change
      survey.value.onValueChanged.add((sender, options) => {
        const question = sender.getQuestionByName(options.name);
        if (question && question.getType() === 'rating') {
          const desc = getDescriptionForRating(question, options.value);
          question.description = desc || '';
        }
      });
    }).catch((err) => {
      const errorMessage = (err.response && err.response.data && err.response.data.error) || 'Something went wrong.'
      proxy.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Error',
          text: errorMessage,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    });

    watch(surveySkin, (newSkin) => {
      if (survey.value) {
        survey.value.applyTheme(themes[newSkin]);
      }
    });

    return { surveySkin, survey };
  }
};
</script>